import { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  AlertCircle,
  Eye,
  Loader2,
  CheckCircle,
  Star,
  ChevronDown,
  ChevronUp,
  Code,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import DesignCanvas from '../../components/DesignCanvas';

const SolutionEditor = () => {
  const [solutions, setSolutions] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    challenge_id: '',
    solution_name: '',
    description: '',
    solution_json: { nodes: [], edges: [] },
    component_list: [],
    is_optimal: true,
    expected_score: 850,
  });

  // Fetch solutions and challenges
  const fetchData = async () => {
    setLoading(true);
    try {
      const [solRes, chalRes] = await Promise.all([
        api.get('/admin/solutions'),
        api.get('/admin/solutions/challenges'),
      ]);
      setSolutions(solRes.data.solutions || []);
      setChallenges(chalRes.data.challenges || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load solutions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      challenge_id: '',
      solution_name: '',
      description: '',
      solution_json: { nodes: [], edges: [] },
      component_list: [],
      is_optimal: true,
      expected_score: 850,
    });
    setSelectedSolution(null);
    setIsEditing(false);
    setIsCreating(false);
    setShowCanvas(false);
    setShowJsonEditor(false);
    setJsonText('');
    setJsonError(null);
  };

  // Toggle JSON editor and sync the text
  const toggleJsonEditor = () => {
    if (!showJsonEditor) {
      // Opening JSON editor - convert current solution_json to text
      setJsonText(JSON.stringify(formData.solution_json, null, 2));
      setJsonError(null);
    }
    setShowJsonEditor(!showJsonEditor);
  };

  // Handle JSON text changes with validation
  const handleJsonChange = (text) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
        setJsonError('JSON must have a "nodes" array');
        return;
      }
      if (!parsed.edges || !Array.isArray(parsed.edges)) {
        setJsonError('JSON must have an "edges" array');
        return;
      }
      setJsonError(null);
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
    }
  };

  // Apply JSON changes to form data
  const applyJsonChanges = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
        toast.error('JSON must have a "nodes" array');
        return;
      }
      if (!parsed.edges || !Array.isArray(parsed.edges)) {
        toast.error('JSON must have an "edges" array');
        return;
      }

      // Extract component IDs from nodes
      const componentIds = parsed.nodes
        .filter(node => node.data?.componentId)
        .map(node => node.data.componentId);

      setFormData(prev => ({
        ...prev,
        solution_json: parsed,
        component_list: componentIds,
      }));

      toast.success('JSON applied successfully!');
      setShowJsonEditor(false);
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
    }
  };

  // Copy JSON to clipboard
  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(formData.solution_json, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('JSON copied to clipboard!');
    } catch (e) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
    setShowCanvas(true);
  };

  const handleEdit = async (solution) => {
    try {
      setProcessing(true);
      const response = await api.get(`/admin/solutions/${solution.id}`);
      const fullSolution = response.data?.solution;

      if (!fullSolution) {
        toast.error('Solution data not found');
        return;
      }

      setSelectedSolution(fullSolution);
      setFormData({
        challenge_id: fullSolution.challenge_id || '',
        solution_name: fullSolution.solution_name || '',
        description: fullSolution.description || '',
        solution_json: fullSolution.solution_json || { nodes: [], edges: [] },
        component_list: fullSolution.component_list || [],
        is_optimal: fullSolution.is_optimal || false,
        expected_score: fullSolution.expected_score || 850,
      });
      setIsEditing(true);
      setShowCanvas(true);
    } catch (err) {
      console.error('Failed to fetch solution:', err);
      toast.error(err.response?.data?.error || 'Failed to load solution details');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this solution?')) return;

    try {
      setProcessing(true);
      await api.delete(`/admin/solutions/${id}`);
      toast.success('Solution deleted');
      fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete solution');
    } finally {
      setProcessing(false);
    }
  };

  // Called when admin saves the canvas design
  const handleCanvasSave = useCallback((designData) => {
    // Extract nodes and edges from the design
    const nodes = designData.design_json?.nodes || [];
    const edges = designData.design_json?.edges || [];
    const componentList = designData.component_list || [];

    setFormData(prev => ({
      ...prev,
      solution_json: { nodes, edges },
      component_list: componentList,
    }));

    toast.success('Design captured! Now save the solution.');
  }, []);

  const handleSave = async () => {
    if (!formData.challenge_id) {
      toast.error('Please select a challenge');
      return;
    }
    if (!formData.solution_name.trim()) {
      toast.error('Solution name is required');
      return;
    }
    if (!formData.solution_json.nodes || formData.solution_json.nodes.length === 0) {
      toast.error('Please design a solution with at least one component');
      return;
    }

    try {
      setProcessing(true);

      const payload = {
        challenge_id: parseInt(formData.challenge_id),
        solution_name: formData.solution_name,
        description: formData.description,
        solution_json: formData.solution_json,
        component_list: formData.component_list.map(id => parseInt(id)).filter(id => !isNaN(id)),
        is_optimal: formData.is_optimal,
        expected_score: parseInt(formData.expected_score) || 850,
      };

      if (isEditing && selectedSolution) {
        await api.put(`/admin/solutions/${selectedSolution.id}`, payload);
        toast.success('Solution updated');
      } else {
        await api.post('/admin/solutions', payload);
        toast.success('Solution created');
      }

      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error(err.response?.data?.error || 'Failed to save solution');
    } finally {
      setProcessing(false);
    }
  };

  const selectedChallenge = challenges.find(c => c.id === parseInt(formData.challenge_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Canvas view for creating/editing
  if (showCanvas) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">
              {isEditing ? 'Edit Solution' : 'Create New Solution'}
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleJsonEditor}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 text-white hover:bg-slate-900 rounded-lg transition-colors font-medium text-sm"
              >
                <Code className="w-4 h-4" />
                {showJsonEditor ? 'Hide JSON Editor' : 'Edit JSON'}
              </button>
              <button
                type="button"
                onClick={copyJsonToClipboard}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium text-sm"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
              <button
                onClick={resetForm}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Challenge *
              </label>
              <select
                value={formData.challenge_id}
                onChange={(e) => setFormData({ ...formData, challenge_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isEditing}
              >
                <option value="">Select a challenge...</option>
                {challenges.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.id} - {c.title} ({c.difficulty})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solution Name *
              </label>
              <input
                type="text"
                value={formData.solution_name}
                onChange={(e) => setFormData({ ...formData, solution_name: e.target.value })}
                placeholder="e.g., Optimal Architecture"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Score
              </label>
              <input
                type="number"
                value={formData.expected_score}
                onChange={(e) => setFormData({ ...formData, expected_score: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe why this solution is optimal..."
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_optimal}
                  onChange={(e) => setFormData({ ...formData, is_optimal: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Mark as Optimal Solution</span>
              </label>
            </div>
          </div>

          {/* Design Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 flex-wrap">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
              {formData.solution_json.nodes?.length || 0} nodes
            </span>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
              {formData.solution_json.edges?.length || 0} edges
            </span>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
              {formData.component_list?.length || 0} components
            </span>
          </div>

          {/* JSON Editor */}
          {showJsonEditor && (
            <div className="mb-4 border rounded-lg overflow-hidden">
              <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  solution_json Editor
                </span>
                <div className="flex items-center gap-2">
                  {jsonError ? (
                    <span className="text-red-400 text-xs flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {jsonError}
                    </span>
                  ) : jsonText && (
                    <span className="text-green-400 text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Valid JSON
                    </span>
                  )}
                </div>
              </div>
              <textarea
                value={jsonText}
                onChange={(e) => handleJsonChange(e.target.value)}
                className="w-full h-96 p-4 font-mono text-sm bg-gray-900 text-gray-100 focus:outline-none resize-y"
                placeholder='{"nodes": [], "edges": []}'
                spellCheck={false}
              />
              <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-t">
                <p className="text-xs text-gray-500">
                  Edit the solution JSON directly. Make sure to include valid "nodes" and "edges" arrays.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowJsonEditor(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyJsonChanges}
                    disabled={!!jsonError}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    Apply Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={processing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEditing ? 'Update Solution' : 'Create Solution'}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-medium text-gray-800">
              Design Canvas
              {selectedChallenge && (
                <span className="ml-2 text-sm text-gray-500">
                  for: {selectedChallenge.title}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Design the reference solution. Click "Submit" to capture the design.
            </p>
          </div>
          <DesignCanvas
            challenge={selectedChallenge}
            onSubmit={handleCanvasSave}
            initialDesign={formData.solution_json}
            isAdminMode={true}
          />
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Reference Solutions</h2>
          <p className="text-gray-500 mt-1">
            Manage optimal solutions that users can view as examples
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Add Solution
        </button>
      </div>

      {/* Solutions List */}
      {solutions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Solutions Yet</h3>
          <p className="text-gray-500 mb-6">
            Create reference solutions for challenges so users can learn from optimal designs.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Create First Solution
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Challenge
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Solution Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Design
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {solutions.map((solution) => (
                <tr key={solution.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      #{solution.challenge_id} - {solution.challenge_title}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{solution.solution_name}</div>
                    {solution.description && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {solution.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {solution.node_count} nodes
                      </span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        {solution.edge_count} edges
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {solution.is_optimal ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                        <Star className="w-3 h-3" />
                        Optimal
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        Alternative
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(solution)}
                        disabled={processing}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(solution.id)}
                        disabled={processing}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SolutionEditor;
